import { sql, type SQL, type SQLWrapper } from 'drizzle-orm';

/**
 * Chat event names, in one place.
 *
 * Every conversational turn is stored as a row in `user_history` whose `event` column names what
 * happened. Two different families of names ended up in that column, and both have to keep working.
 *
 * The first family is the one the product itself writes and has always written: `user chat` for a
 * turn from the person, `agent chat` for a turn from the AI, plus channel-suffixed variants such as
 * `user chat message whatsapp` and `agent chat message`. Every row of live conversation data uses
 * this family, so it is the canonical one and it is what new code should write.
 *
 * The second family came from the published Public API documentation, which told external
 * integrators to append history entries named `user_message` and `assistant_message`. Neither of
 * those contains the substring "chat", so conversations written by a conforming integration were
 * invisible to the product: they never rendered in the chat panel, never came back from the
 * chat-messages endpoint, and counted zero in every dashboard chart. A few internal call sites also
 * wrote `user_chat` and `assistant_chat`, which had the same problem in the places that matched on
 * the exact phrase "user chat" rather than on the substring "chat".
 *
 * The fix is to widen every read so it recognises both families, and to correct the documentation so
 * new integrations write the canonical names. Rows already in the database keep the names they were
 * written with forever — there is no rename migration that would make the alias names go away — so
 * this dual-read is permanent, not a temporary compatibility shim. Do not narrow any of the
 * predicates below on the assumption that the aliases have been cleaned up.
 *
 * Matching rules, so the SQL and the TypeScript agree:
 *   - Canonical names match as a substring, because the channel-suffixed variants have to match too.
 *   - Alias names match as a prefix, which allows the same kind of suffixing without letting an
 *     unrelated event name that merely contains "user_chat" somewhere in the middle slip through.
 *   - Takeover events (`takeover started`, `takeover ended`, ...) are part of the conversation
 *     stream and are attributed to the assistant, matching how they have always been folded into
 *     LLM history as system context.
 */

/** Canonical event name for a turn from the person the agent is talking to. */
export const USER_CHAT_EVENT = 'user chat';

/** Canonical event name for a turn from the agent. Historically "agent", not "assistant". */
export const AGENT_CHAT_EVENT = 'agent chat';

/** Takeover events are conversation-stream events even though they carry no chat name. */
export const TAKEOVER_EVENT_PREFIX = 'takeover';

/** Non-canonical names accepted on read for a turn from the person. */
export const USER_CHAT_EVENT_ALIASES: readonly string[] = ['user_message', 'user_chat'];

/** Non-canonical names accepted on read for a turn from the agent. */
export const ASSISTANT_CHAT_EVENT_ALIASES: readonly string[] = ['assistant_message', 'assistant_chat'];

/**
 * Escape the characters LIKE treats as wildcards. This matters for the aliases: without escaping,
 * `user_message%` would also match `user message...` and `userXmessage...`, because `_` is LIKE's
 * single-character wildcard. Postgres uses backslash as the LIKE escape character by default, so
 * the escaped pattern needs no explicit ESCAPE clause.
 */
function escapeLikeLiteral(value: string): string {
	return value.replace(/[\\%_]/g, '\\$&');
}

function likePrefixPattern(prefix: string): string {
	return `${escapeLikeLiteral(prefix)}%`;
}

function likeContainsPattern(fragment: string): string {
	return `%${escapeLikeLiteral(fragment)}%`;
}

/** LIKE patterns matching any turn from the person, canonical or aliased. */
export const USER_CHAT_EVENT_LIKE_PATTERNS: readonly string[] = [
	likeContainsPattern(USER_CHAT_EVENT),
	...USER_CHAT_EVENT_ALIASES.map(likePrefixPattern),
];

/** LIKE patterns matching any turn from the agent, canonical or aliased. */
export const ASSISTANT_CHAT_EVENT_LIKE_PATTERNS: readonly string[] = [
	likeContainsPattern(AGENT_CHAT_EVENT),
	...ASSISTANT_CHAT_EVENT_ALIASES.map(likePrefixPattern),
];

/**
 * LIKE patterns matching everything that belongs in a conversation transcript. The bare `%chat%`
 * entry is deliberately broad: it is what the product has always used, so narrowing it would drop
 * rows that are being read today.
 */
export const CHAT_EVENT_LIKE_PATTERNS: readonly string[] = [
	likeContainsPattern('chat'),
	likePrefixPattern(TAKEOVER_EVENT_PREFIX),
	...USER_CHAT_EVENT_ALIASES.map(likePrefixPattern),
	...ASSISTANT_CHAT_EVENT_ALIASES.map(likePrefixPattern),
];

function startsWithAny(event: string, prefixes: readonly string[]): boolean {
	return prefixes.some((prefix) => event.startsWith(prefix));
}

/** True for takeover events, which sit in the conversation stream but carry no chat name. */
export function isTakeoverEvent(event: string): boolean {
	return !!event && event.startsWith(TAKEOVER_EVENT_PREFIX);
}

/** True for any event that represents a turn from the person the agent is talking to. */
export function isUserChatEvent(event: string): boolean {
	if (!event) return false;
	return event.includes(USER_CHAT_EVENT) || startsWithAny(event, USER_CHAT_EVENT_ALIASES);
}

/** True for any event that represents a turn from the agent. */
export function isAssistantChatEvent(event: string): boolean {
	if (!event) return false;
	return event.includes(AGENT_CHAT_EVENT) || startsWithAny(event, ASSISTANT_CHAT_EVENT_ALIASES);
}

/**
 * True for drafted-but-not-sent turns ('agent chat suggestion', channel-suffixed variants).
 * These render in the transcript UI but must not feed LLM history: an unapproved draft never
 * reached the person, and an approved one is already represented by the delivered
 * 'agent chat message' row the approve route writes — including both would double it.
 */
export function isSuggestionEvent(event: string): boolean {
	return !!event && event.includes('suggestion');
}

/** Exact-match note family (docs/database.md "user_history event shape"); notes are not chat turns. */
export function isNoteEvent(event: string): boolean {
	return event === 'note';
}

/** True for anything that belongs in a conversation transcript, including takeover events. */
/** Whether loaded history holds any chat message; edits, tool calls and other events do not count. */
export function hasChatMessages(history: readonly { event: string }[] | undefined): boolean {
	return !!history?.some((row) => isChatEvent(row.event));
}

export function isChatEvent(event: string): boolean {
	if (!event) return false;
	if (event.includes('chat')) return true;
	if (isTakeoverEvent(event)) return true;
	return isUserChatEvent(event) || isAssistantChatEvent(event);
}

/**
 * Which side of the conversation an event belongs to. Takeover events are attributed to the
 * assistant because that is how they have always been injected into LLM history. Anything that is
 * not recognised as a user turn falls through to the assistant, which preserves the long-standing
 * behaviour of treating "does the event name mention the user?" as the deciding question.
 */
export function chatEventRole(event: string): 'user' | 'assistant' {
	if (isTakeoverEvent(event)) return 'assistant';
	if (startsWithAny(event, USER_CHAT_EVENT_ALIASES)) return 'user';
	if (startsWithAny(event, ASSISTANT_CHAT_EVENT_ALIASES)) return 'assistant';
	return event.includes('user') ? 'user' : 'assistant';
}

function likeAnyPredicate(eventColumn: SQLWrapper, patterns: readonly string[]): SQL {
	const conditions = patterns.map((pattern) => sql`${eventColumn} LIKE ${pattern}`);
	return sql`(${sql.join(conditions, sql` OR `)})`;
}

/**
 * Drizzle predicate for "this row belongs in a conversation transcript". Accepts either a Drizzle
 * column (`userHistory.event`) or a raw fragment for aliased raw SQL (`` sql`uh.event` ``).
 */
export function chatEventSqlPredicate(eventColumn: SQLWrapper): SQL {
	return likeAnyPredicate(eventColumn, CHAT_EVENT_LIKE_PATTERNS);
}

/** Drizzle predicate for turns from the person, canonical or aliased. */
export function userChatEventSqlPredicate(eventColumn: SQLWrapper): SQL {
	return likeAnyPredicate(eventColumn, USER_CHAT_EVENT_LIKE_PATTERNS);
}

/** Drizzle predicate for turns from the agent, canonical or aliased. */
export function assistantChatEventSqlPredicate(eventColumn: SQLWrapper): SQL {
	return likeAnyPredicate(eventColumn, ASSISTANT_CHAT_EVENT_LIKE_PATTERNS);
}

/** The transports a conversation row can arrive on or depart over. */
export type ChatChannel = 'whatsapp' | 'sms' | 'imessage' | 'email' | 'slack' | 'web';

const CHAT_CHANNELS: ReadonlySet<string> = new Set([
	'whatsapp',
	'sms',
	'imessage',
	'email',
	'slack',
	'web',
]);

/** Names stored rows use that are not channel names: typed metadata says the vendor transport, `data.channel` says the vendor. */
const CHANNEL_ALIASES: Record<string, ChatChannel> = {
	sms_twilio: 'sms',
	gmail: 'email',
};

function asChatChannel(value: unknown): ChatChannel | null {
	if (typeof value !== 'string' || value === '') return null;
	const normalized = value.toLowerCase();
	const aliased = CHANNEL_ALIASES[normalized];
	if (aliased) return aliased;
	return CHAT_CHANNELS.has(normalized) ? (normalized as ChatChannel) : null;
}

/**
 * Which channel a history row travelled over. `data.channelType` is the explicit stamp every turn
 * written by `runChannelChat` now carries and is always trusted first. Rows written before that
 * recorded provenance three other ways, all still in the data — typed `data.channelMetadata`, the
 * older `data.channel` vendor string, and the oldest channel-suffixed event name — so they are
 * checked in turn, newest-first. The suffix check matches the event's final word only; a channel
 * name elsewhere in an event name does not count. Null means the row carries no channel identity.
 */
export function channelOfEvent(row: { event: string; data?: any }): ChatChannel | null {
	const fromStamp = asChatChannel(row.data?.channelType);
	if (fromStamp) return fromStamp;
	const fromMetadata = asChatChannel(row.data?.channelMetadata?.type);
	if (fromMetadata) return fromMetadata;
	const fromLegacyChannel = asChatChannel(row.data?.channel);
	if (fromLegacyChannel) return fromLegacyChannel;
	const lastToken = row.event ? row.event.trim().split(/\s+/).pop() : undefined;
	return asChatChannel(lastToken);
}
